import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Single axios instance that always sends cookies (JWT via httpOnly cookies).
// Auto-refresh on 401 for staff and customer tokens.
export const api = axios.create({ baseURL: API_BASE, withCredentials: true });

// Attach the "Fix & resubmit" HISAB id (when the CSA is on that flow) so
// backend can route ALL draft-scoped calls to the reopened HISAB instead of
// the CSA's current active-shift draft.
// The frontend puts `?fix=<hisab_id>` on the URL when navigating to the fix
// flow; the interceptor picks it up here.
api.interceptors.request.use((cfg) => {
  try {
    const sp = new URLSearchParams(window.location.search);
    const fixId = sp.get("fix");
    if (fixId) {
      cfg.headers = cfg.headers || {};
      cfg.headers["X-HisabX-Fix-Id"] = fixId;
    }
  } catch (_) { /* ignore */ }
  return cfg;
});

let refreshInFlight = null;

async function tryRefresh(url) {
  // Prevent parallel refresh calls
  if (!refreshInFlight) {
    // Guess which flow we need to refresh based on request URL
    const isCustomer = url.startsWith("/customer") && !url.startsWith("/customer/login");
    refreshInFlight = api.post(isCustomer ? "/customer/refresh" : "/auth/refresh")
      .finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config || {};
    const status = error.response?.status;
    if (status === 401 && !cfg._retried) {
      // Never try to refresh on the refresh/login endpoints themselves
      const url = cfg.url || "";
      const skip =
        url.includes("/auth/refresh") ||
        url.includes("/customer/refresh") ||
        url.includes("/auth/login") ||
        url.includes("/customer/login") ||
        url.includes("/auth/register") ||
        url.includes("/invites/accept");
      if (!skip) {
        try {
          await tryRefresh(url);
          cfg._retried = true;
          return api.request(cfg);
        } catch (_) { /* fall through */ }
      }
    }
    return Promise.reject(error);
  },
);

// Alias for backward compat with earlier code
export const staffApi = api;
export const customerApi = api;

export async function downloadPdf(url, { filename } = {}) {
  const resp = await api.get(url, { responseType: "blob" });
  const blob = new Blob([resp.data], { type: "application/pdf" });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename || "download.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 60_000);
}

export async function openPdf(url) {
  const resp = await api.get(url, { responseType: "blob" });
  const blob = new Blob([resp.data], { type: "application/pdf" });
  const href = URL.createObjectURL(blob);
  window.open(href, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(href), 60_000);
}

// Utility for FastAPI error → string
export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  }
  if (typeof detail?.msg === "string") return detail.msg;
  return String(detail);
}
